/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useEffect, useContext } from "react";
import isEqual from "react-fast-compare";
import { useTranslation } from "react-i18next";
import { TextField } from "@mui/material";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { Logger, red } from "cms/utils/log/logger";
import { useAuth } from "cms/auth/hooks/use-auth";
import LabelledField from "components/labelled-field/index";
import { SimplicityComposerEnsureTipTap } from "cms/rich-text/simplicity-composer-tiptap/index";
import { ComponentCarouselContext } from "cms/components/components/hero-carousel/edit/component-carousel-context";
import { CarouselImageEditor } from "cms/components/components/hero-carousel/edit/image/carousel-image-editor";
import { isGuid, isStringEmpty } from "utils/empty-exists";
import { LinkSelectorWrapper } from "cms/links/link-selector/link-selector-wrapper";
import { cloneObjectsArray } from "utils/array/index";
import { FieldErrorFallback } from "cms/fields/layout/field-error";
import { ErrorBoundary } from "react-error-boundary";

/**
 *    This is the slide editor component
 * */
const HeroCarouselSlideEditor = () => {

    const { t } = useTranslation();
    const { token } = useAuth();

    const {
        list,
        item,
        index,
        // position,
        updateItems,
    } = useContext(ComponentCarouselContext);

    const {
        title: _title,
        description: _description,
        link: _link,
        image: _image,
    } = item || {};

    const { image: initial_image_guid, settings: _settings } = _image || {};

    const [loadingLink,setLoadingLink] = useState();
    const [imageLoading,setImageLoading] = useState(false);
    const [imageFailed,setImageFailed] = useState(false);

    const [title,setTitle] = useState(_title);
    const [description,setDescription] = useState(_description);
    const [link,setLink] = useState(_link);
    const [image,setImage] = useState();
    const [settings,setSettings] = useState(_settings);

    useEffect(
        () => {
            // avoid updates if the description field has hot changed
            if (!isEqual(_description, description)) {
                const nextSlides = cloneObjectsArray(list);
                const image_guid = image?.image ? image?.image : initial_image_guid;
                // Logger.log(_image, image)
                nextSlides[index].title = title;
                nextSlides[index].link = link;
                nextSlides[index].image = {
                    image: image_guid,
                    settings
                };
                nextSlides[index].description = description;
                updateItems(nextSlides);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [description]
    );

    const onChangeTitle = (event) => {
        const title = event.target.value;
        // Logger.debug(`[onChangeTitle] title`, title)
        setTitle(title);
        const nextSlides = cloneObjectsArray(list);
        nextSlides[index].title = title;
        updateItems(nextSlides);
    };
    const onChangeDescription = (description) => {
        // Logger.debug(`[onChangeDescription] description`, description)
        // Due to stale closure with rich text editor - using useEffect()
        setDescription(description);
    };
    const onChangeLink = (link) => {
        // Logger.debug(`[onChangeLink] link`, link)
        setLink(link);
        const nextSlides = cloneObjectsArray(list);
        nextSlides[index].link = link;
        updateItems(nextSlides);
    };
    const onRemoveLink = () => {
        const link = undefined;
        // Logger.debug(`[onRemoveLink] link`, link)
        setLink(link);
        const nextSlides = cloneObjectsArray(list);
        nextSlides[index].link = link;
        updateItems(nextSlides);
    };
    const onChangeImageData = ( imageData ) => {
        // console.debug(`[HeroCarouselSlideEditor] [onChangeImageData] image`, index, imageData)
        // console.debug(`[HeroCarouselSlideEditor] [onChangeImageData] list`, list)
        const { image, settings } = imageData;    // image is object here
        setImage(image);
        setSettings(settings);
        const { guid } = image || {};
        // console.info(`[onChangeImageData] [index:${index}] [guid:${guid}]`, image)
        if (isStringEmpty(guid)) {
            Logger.warn("[onChangeImageData] image guid missing", index, image, guid);
        } else {
            const newImageData = {
                image: guid,   // image is guid here
                settings
            };
            // console.info(`[onChangeImageData] newImageData [index:${index}] [guid:${guid}]`, newImageData)
            const nextSlides = cloneObjectsArray(list);
            nextSlides[index].image = newImageData;
            Logger.info(red(`[onChangeImageData] next slides ${index}`), nextSlides);
            updateItems(nextSlides);
            setImageFailed(false);
        }
    };
    const onRemoveImage = () => {
        Logger.debug("[onRemoveImage]");
        setImage(undefined);
        setSettings(undefined);
        const newImageData = {
            image: undefined,
            settings: undefined
        };
        const nextSlides = cloneObjectsArray(list);
        nextSlides[index].image = newImageData;
        updateItems(nextSlides);
        setImageFailed(false);
    };

    const fetchImage = async (guid) => {
        if (isGuid(guid)) {
            setImageLoading(true);
            try {
                let currentImage = await SimpliCityAdminSDK.image.findOne(token, guid);
                setImage(currentImage);
                setImageLoading(false);
            } catch (err) {
                Logger.error(err);
                setImageFailed(true);
            }
        } else {
            setImageLoading(false);
            setImageFailed(false);
        }
    };

    useEffect(() => {
        fetchImage(initial_image_guid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial_image_guid]);

    return (
        <div className='simplicity-editor-frame hero-editor-container'>
            <div className='component-editor-image-wrapper'>
                <div className='component-editor-image-wrapper-cell'>
                    {!imageLoading &&
                        <CarouselImageEditor
                            image={image}
                            image_guid={image?.guid}
                            settings={settings}
                            onChangeImageData={onChangeImageData}
                            onRemoveImage={onRemoveImage}
                        />
                    }
                    {imageFailed &&
                        <>
                            <CarouselImageEditor
                                image={undefined}
                                image_guid={undefined}
                                settings={settings}
                                onChangeImageData={onChangeImageData}
                                onRemoveImage={onRemoveImage}
                            />
                        </>
                    }
                </div>
                <div className='component-editor-image-details-wrapper-cell'>

                    <LabelledField label={`${t("commons.title")} :`}>
                        {/*  Title  */}
                        <TextField
                            id='carousel-title'
                            fullWidth={true}
                            value={title}
                            placeholder={t("commons.addTitleOptional")}
                            onChange={onChangeTitle}
                        />
                    </LabelledField>

                    <LabelledField label={`${t("commons.link")} :`} className='hero-editor-link'>
                        {/*  Link  */}
                        <div style={{ flex: "1" }}>
                            <LinkSelectorWrapper
                                link={link}
                                setLink={setLink}
                                onLinkChange={(link) => onChangeLink(link)}
                                onLinkRemove={() => onRemoveLink()}
                                disabled={false}
                                loadingLink={loadingLink}
                                setLoadingLink={setLoadingLink}
                            />
                        </div>
                    </LabelledField>

                </div>
            </div>

            <LabelledField label={t("commons.description")}>
                {/*  Description  */}
                <ErrorBoundary
                    FallbackComponent={FieldErrorFallback}
                    onReset={() => {
                        location.reload();
                    }}
                >
                    <SimplicityComposerEnsureTipTap
                        richtext={description}
                        onChange={onChangeDescription}
                    />
                </ErrorBoundary>
            </LabelledField>

            {/*<details style={{overflowX: 'scroll', background: '#fafafa', fontSize: '9px', maxHeight: '800px', width: '800px', display: 'block'}}>*/}
            {/*    <pre>{JSON.stringify(item, null, 2)}</pre>*/}
            {/*</details>*/}

        </div>
    );
};
HeroCarouselSlideEditor.displayName = "HeroCarouselSlideEditor";
export { HeroCarouselSlideEditor };
